import FixedDeposits from '@components/Products/FixedDeposits';
import { fdCarouselContent, fdCompareContent } from '@components/Products/FixedDeposits/utils/helper';
import { HeadTagData } from '@helpers/headTagData';
import { isFDFeatureEnabled } from '@helpers/homepageData';
import { getOpenConfigurationByEntityType } from '@services/configuration.service';
import { getFdInstitutionsOpen } from '@services/fd.service';
import { getFdsMaxInterest } from '@services/fd.service';
import NextHeadTag from '@ui/molecules/NextHeadTag';
import {
    setReferralCode,
    setReferralEntityType,
} from '@services/identity.service';
import { useRouter } from 'next/router';
import { getQueryParamsFromUrl } from '@helpers/miscelleanous';
import { useEffect } from 'react';
import { isValidResponseEntity } from '@ui/helpers/utils';
import { DEFAULT_MAX_INTEREST_RATE } from '@helpers/constants';

const FixedDepositsPage = ({ fdDetails, maxInterest }) => {
    const headTagProps = HeadTagData?.[`/products/fixed-deposit`] || {};
    const data = fdDetails?.value;
    const router = useRouter();

    useEffect(() => {
        const referralCode =
            router.query?.referrer ||
            router.query?.usoc ||
            getQueryParamsFromUrl(window.location.search, 'usoc') ||
            getQueryParamsFromUrl(window.location.search, 'referrer');

        if (referralCode) {
            if (
                router.query?.usoc ||
                getQueryParamsFromUrl(window.location.search, 'usoc')
            ) {
                setReferralEntityType('usoc');
            } else {
                setReferralEntityType('referrer');
            }
            setReferralCode(referralCode);
        }
    }, []);

    return (
        <div>
            <NextHeadTag {...headTagProps} />
            <FixedDeposits
                fixedDepositBanks={data?.carouselContent}
                fdCompareContent={data?.fdCompareContent}
                maxInterest={maxInterest}
            />
        </div>
    );
};

const createCarouselFromInstitutions = (rows) => {
    return rows.map((r) => ({
        name: r.name,
        returns: `${r.maxInterest}%`,
        icon: r.logo || null,
        bg: r.gradient || 'linear-gradient(0deg, #FFF 60.18%, #F0DEFF 100%)'
    }));
};

export const getStaticProps = async (ctx) => {
    const defaultFixedDepositBanks = {
        type: 'General',
        entityType: 'FixedDepositsBanks',
        value: {
            carouselContent: fdCarouselContent.cards,
            fdCompareContent: fdCompareContent
        }
    };

    try {
        if (!isFDFeatureEnabled) {
            return {
                notFound: true,
            };
        }

        const [configResponse, maxInterestResponse, institutionsResponse] = await Promise.all([
            getOpenConfigurationByEntityType('FixedDepositsBanks', ctx),
            getFdsMaxInterest(),
            getFdInstitutionsOpen(ctx)
        ]);

        const maxInterest = isValidResponseEntity(maxInterestResponse) && maxInterestResponse.entity.maxInterest
            ? maxInterestResponse.entity.maxInterest
            : null;

        const institutionsRows = isValidResponseEntity(institutionsResponse) && Array.isArray(institutionsResponse.entity?.rows)
            ? institutionsResponse.entity.rows
            : [];

        const fdDetails = configResponse?.entity || defaultFixedDepositBanks;
        if (typeof fdDetails.value === 'string') {
            fdDetails.value = JSON.parse(fdDetails.value || '{}');
        }

        const configMaxInterest = fdDetails?.value?.maxInterest;
        const finalMaxInterest = maxInterest || configMaxInterest || DEFAULT_MAX_INTEREST_RATE;

        if (!isValidResponseEntity(configResponse)) {
            if (institutionsRows.length > 0) {
                return {
                    props: {
                        fdDetails: {
                            value: {
                                carouselContent: createCarouselFromInstitutions(institutionsRows),
                                fdCompareContent: defaultFixedDepositBanks.value.fdCompareContent,
                            },
                        },
                        maxInterest: finalMaxInterest,
                    },
                    revalidate: 86400,
                };
            }

            return {
                props: {
                    fdDetails: { value: defaultFixedDepositBanks.value },
                    maxInterest: finalMaxInterest,
                },
                revalidate: 86400,
            };
        }

        if (institutionsRows.length > 0) {
            fdDetails.value.carouselContent = createCarouselFromInstitutions(institutionsRows);
        }

        return {
            props: { fdDetails, maxInterest: finalMaxInterest },
            revalidate: 86400,
        };
    } catch (error) {
        return {
            props: {
                fdDetails: { value: defaultFixedDepositBanks.value },
                maxInterest: DEFAULT_MAX_INTEREST_RATE,
            },
            revalidate: 86400,
        };
    }
};

export default FixedDepositsPage;
